/* eslint-disable no-nested-ternary */
import { FilterFilled, FilterOutlined } from '@ant-design/icons';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  Badge,
  Button,
  Checkbox,
  DatePicker,
  Divider,
  Empty,
  Form,
  Modal,
  Switch,
  Tag,
  Tooltip,
} from 'antd';
import dayjs from 'dayjs';
import { debounce, filter, forEach, isEmpty, map } from 'lodash';
import { React, useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { AppContext } from '../../../../../AppContext';
import { ReactComponent as ArchiveIcon } from '../../../../../assets/svg/box-archive-solid.svg';
import { ReactComponent as ApproveIcon } from '../../../../../assets/svg/circle-check-solid.svg';
import { ReactComponent as RejectIcon } from '../../../../../assets/svg/circle-xmark-solid.svg';
import { ReactComponent as EditIcon } from '../../../../../assets/svg/pen-to-square-solid.svg';
import { ReactComponent as UnarchiveIcon } from '../../../../../assets/svg/right-from-bracket-solid.svg';
import {
  ARCHIVING_STATE,
  BREAKPOINTS,
  DATE_FORMATS,
  LIMIT,
  PERMISSIONS_KEY,
  PERMISSION_TYPE,
  REQUEST_STATUS,
  REQUEST_TYPES,
  ROUTES,
  SORT,
  STATUS_FILTER,
} from '../../../../../common/constants';
import { formatDate, hasPermission } from '../../../../../common/utils';
import CommonTable from '../../../../../components/CommonTable';
import LoaderComponent from '../../../../../components/LoaderComponent';
import SearchComponent from '../../../../../components/SearchComponent';
import { UPDATE_REQUEST_ADMIN } from '../../../graphql/Mutations';
import { CREATE_REQUEST_LIBRARY_ADMIN } from '../graphql/Mutations';
import { CLOSURE_REQUESTS_ADMIN, LIBRARIES } from '../graphql/Queries';
import RaiseClosureRequestModal from './RaiseClosureRequestModal';

const { RangePicker } = DatePicker;

const initialPaginationValue = {
  total: 0,
  current: 1,
  pageSize: 10,
};

const initialAddressFilter = {
  limit: 10,
  search: '',
  skip: 0,
};

const initialAddressSort = {
  field: 'createdAt',
  order: 'DESC',
};

const initialTableFilter = {
  skip: 0,
  limit: LIMIT,
  search: '',
};

const initialTableSort = {
  field: 'createdAt',
  order: 'DESC',
};

let filterScrollDebounceJob = null;

const ClosureRequestsTable = ({
  exportFilter,
  setExportFilter,
  isRaiseRequestModalOpen,
  setIsRaiseRequestModalOpen,
}) => {
  const {
    state: {
      permissions,
      commonPermissions: { isLibraryPermission },
    },
  } = useContext(AppContext);
  const [form] = Form.useForm();
  const history = useHistory();
  const [closureReqList, setClosureReqList] = useState([]);
  const [closureReqSearchTerm, setClosureReqSearchTerm] = useState('');
  const [isEmptyClosureReqList, setIsEmptyClosureReqList] = useState(false);
  const [closureReq, setClosureReq] = useState(null);
  const [query, setQuery] = useState('');
  const [sortedInfo, setSortedInfo] = useState({});
  const [paginationProp, setPaginationProp] = useState(initialPaginationValue);

  const [isArchiveLoading, setIsArchiveLoading] = useState(false);
  const [isArchivePrompts, setIsArchivePrompts] = useState(false);
  const [isApproveLoading, setIsApproveLoading] = useState(false);
  const [isApprovePrompts, setIsApprovePrompts] = useState(false);
  const [isRejectLoading, setIsRejectLoading] = useState(false);
  const [isRejectPrompts, setIsRejectPrompts] = useState(false);
  const [isBackupLibLoading, setIsBackupLibLoading] = useState(false);
  const [isBackupLibPrompts, setIsBackupLibPrompts] = useState(false);

  const [isClosureReqLoading, setIsClosureReqLoading] = useState(true);
  const [isArchiveFilter, setIsArchiveFilter] = useState(false);
  const [filterSearch, setFilterSearch] = useState('');
  const [filterList, setFilterList] = useState([]);
  const [whereFilter, setWhereFilter] = useState();
  const [filterLoading, setFilterLoading] = useState(false);
  const [filterTags, setFilterTags] = useState(null);
  const [filterVisible, setFilterVisible] = useState(false);
  const [filterIndex, setFilterIndex] = useState(null);
  const [finalWhereFilters, setFinalWhereFilters] = useState();
  const [finalFilterTags, setFinalFilterTags] = useState(null);
  const [filterIsEnd, setFilterIsEnd] = useState(false);
  const [isEmptyFilterList, setIsEmptyFilterList] = useState(false);
  const [isRaiseRequestLoading, setIsRaiseRequestLoading] = useState(false);

  const [requestsAdmin] = useLazyQuery(CLOSURE_REQUESTS_ADMIN, {
    onCompleted: (response) => {
      setClosureReqList([...response?.requestsAdmin?.data]);
      if (
        response?.requestsAdmin?.count === 0 &&
        initialPaginationValue?.total === 0
      ) {
        setIsEmptyClosureReqList(true);
      } else {
        setIsEmptyClosureReqList(false);
      }
      const pagination = {
        ...paginationProp,
        defaultPageSize: LIMIT,
        total: response?.requestsAdmin?.count,
      };
      setPaginationProp(pagination);
      setIsClosureReqLoading(false);
    },
    fetchPolicy: 'network-only',
    onError() {},
  });

  const [libraries, { loading: isLibrariesLoading }] = useLazyQuery(LIBRARIES, {
    onCompleted: (response) => {
      if (response?.libraries?.data?.length < LIMIT) {
        setFilterIsEnd(true);
      }
      if (response?.libraries?.count === 0) {
        setIsEmptyFilterList(true);
      } else {
        setIsEmptyFilterList(false);
      }
      const optionsCopy = [];
      forEach(response?.libraries?.data, (item) =>
        optionsCopy?.push({ label: item?.name, key: item?.id }),
      );
      setFilterList([...filterList, ...optionsCopy]);
      setFilterLoading(false);
    },
    fetchPolicy: 'network-only',
    onError() {
      setFilterLoading(false);
    },
  });

  const [updateRequestAdmin] = useMutation(UPDATE_REQUEST_ADMIN, {
    onError: () => {},
  });

  const [createRequestLibraryAdmin] = useMutation(
    CREATE_REQUEST_LIBRARY_ADMIN,
    {
      onError: () => {},
    },
  );

  const hasUpdatePermission = hasPermission(
    PERMISSIONS_KEY?.REQUESTS_LIBRARY_CLOSURE_REQUESTS,
    PERMISSION_TYPE?.UPDATE,
    permissions,
  );

  useEffect(() => {
    setPaginationProp({ ...paginationProp, current: 1, skip: 0 });
    setIsClosureReqLoading(true);
    requestsAdmin({
      variables: {
        filter: {
          ...initialAddressFilter,
          limit: paginationProp?.pageSize || LIMIT,
          search: closureReqSearchTerm,
          isArchived: isArchiveFilter,
          type: REQUEST_TYPES?.LIBRARY_CLOSURE,
          dateRange: {
            from: finalWhereFilters?.createdAt?.from?.$d,
            to: finalWhereFilters?.createdAt?.to?.$d,
          },
          actionTakenOn: {
            from: finalWhereFilters?.actionTakenOn?.from?.$d,
            to: finalWhereFilters?.actionTakenOn?.to?.$d,
          },
          status:
            finalWhereFilters?.status?.length > 0
              ? finalWhereFilters?.status
              : undefined,
          libraryIds: finalWhereFilters?.name,
          backUpLibraryIds: finalWhereFilters?.backUpLibName,
          closureRequestToDateRange: {
            to: finalWhereFilters?.toDate?.date?.$d,
          },
          closureRequestFromDateRange: {
            from: finalWhereFilters?.fromDate?.date?.$d,
          },
        },
        sort: sortedInfo?.column
          ? {
              field: sortedInfo?.field,
              order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialAddressSort,
      },
    });
    setExportFilter({ ...exportFilter, isArchived: isArchiveFilter });
  }, [isArchiveFilter]);

  const handleTableChange = (pagination, tableFilter, sorter) => {
    const { current } = pagination;
    const skip = (current - 1) * (pagination?.pageSize || 0);
    setPaginationProp({ ...paginationProp, ...pagination });
    setIsClosureReqLoading(true);
    setSortedInfo(sorter);
    requestsAdmin({
      variables: {
        filter: {
          ...initialAddressFilter,
          skip,
          limit: pagination?.pageSize,
          search: closureReqSearchTerm,
          isArchived: isArchiveFilter,
          type: REQUEST_TYPES?.LIBRARY_CLOSURE,
          dateRange: {
            from: finalWhereFilters?.createdAt?.from?.$d,
            to: finalWhereFilters?.createdAt?.to?.$d,
          },
          actionTakenOn: {
            from: finalWhereFilters?.actionTakenOn?.from?.$d,
            to: finalWhereFilters?.actionTakenOn?.to?.$d,
          },
          status:
            finalWhereFilters?.status?.length > 0
              ? finalWhereFilters?.status
              : undefined,
          libraryIds: finalWhereFilters?.name,
          backUpLibraryIds: finalWhereFilters?.backUpLibName,
          closureRequestToDateRange: {
            to: finalWhereFilters?.toDate?.date?.$d,
          },
          closureRequestFromDateRange: {
            from: finalWhereFilters?.fromDate?.date?.$d,
          },
        },
        sort: sorter?.column
          ? {
              field: sorter?.field,
              order: sorter?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialAddressSort,
      },
    });
  };

  const handleSearch = (value) => {
    const trimValue = value?.trim();
    setClosureReqSearchTerm(trimValue);
    setPaginationProp({ ...paginationProp, current: 1, skip: 0 });
    setIsClosureReqLoading(true);
    requestsAdmin({
      variables: {
        filter: {
          ...initialAddressFilter,
          limit: paginationProp?.pageSize || LIMIT,
          search: trimValue,
          isArchived: isArchiveFilter,
          type: REQUEST_TYPES?.LIBRARY_CLOSURE,
          dateRange: {
            from: finalWhereFilters?.createdAt?.from?.$d,
            to: finalWhereFilters?.createdAt?.to?.$d,
          },
          actionTakenOn: {
            from: finalWhereFilters?.actionTakenOn?.from?.$d,
            to: finalWhereFilters?.actionTakenOn?.to?.$d,
          },
          status:
            finalWhereFilters?.status?.length > 0
              ? finalWhereFilters?.status
              : undefined,
          libraryIds: finalWhereFilters?.name,
          backUpLibraryIds: finalWhereFilters?.backUpLibName,
          closureRequestToDateRange: {
            to: finalWhereFilters?.toDate?.date?.$d,
          },
          closureRequestFromDateRange: {
            from: finalWhereFilters?.fromDate?.date?.$d,
          },
        },
        sort: sortedInfo?.column
          ? {
              field: sortedInfo?.field,
              order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialAddressSort,
      },
    });
    setExportFilter({ ...exportFilter, search: trimValue });
  };

  const handleArchive = async (id, isArchived) => {
    setIsArchiveLoading(true);
    const response = await updateRequestAdmin({
      variables: {
        where: {
          id,
        },
        data: {
          isArchived: !isArchived,
        },
      },
    });
    if (response?.data) {
      setIsArchiveLoading(false);
      setIsClosureReqLoading(true);
      setIsArchivePrompts(false);
      setClosureReq(null);
      requestsAdmin({
        variables: {
          filter: {
            ...initialAddressFilter,
            skip:
              (paginationProp?.current - 1) * (paginationProp?.pageSize || 0),
            limit: paginationProp?.pageSize || LIMIT,
            search: closureReqSearchTerm,
            isArchived: isArchiveFilter,
            type: REQUEST_TYPES?.LIBRARY_CLOSURE,
            dateRange: {
              from: finalWhereFilters?.createdAt?.from?.$d,
              to: finalWhereFilters?.createdAt?.to?.$d,
            },
            actionTakenOn: {
              from: finalWhereFilters?.actionTakenOn?.from?.$d,
              to: finalWhereFilters?.actionTakenOn?.to?.$d,
            },
            status:
              finalWhereFilters?.status?.length > 0
                ? finalWhereFilters?.status
                : undefined,
            libraryIds: finalWhereFilters?.name,
            backUpLibraryIds: finalWhereFilters?.backUpLibName,
            closureRequestToDateRange: {
              to: finalWhereFilters?.toDate?.date?.$d,
            },
            closureRequestFromDateRange: {
              from: finalWhereFilters?.fromDate?.date?.$d,
            },
          },
          sort: sortedInfo?.column
            ? {
                field: sortedInfo?.field,
                order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
              }
            : initialAddressSort,
        },
      });
    }
    setIsArchiveLoading(false);
  };

  const handleApprove = async (id) => {
    setIsApproveLoading(true);
    const response = await updateRequestAdmin({
      variables: {
        where: {
          id,
        },
        data: {
          status: REQUEST_STATUS?.APPROVED?.value,
        },
      },
    });
    if (response?.data) {
      setIsClosureReqLoading(true);
      setIsApprovePrompts(false);
      setClosureReq(null);
      requestsAdmin({
        variables: {
          filter: {
            ...initialAddressFilter,
            skip:
              (paginationProp?.current - 1) * (paginationProp?.pageSize || 0),
            limit: paginationProp?.pageSize || LIMIT,
            search: closureReqSearchTerm,
            isArchived: isArchiveFilter,
            type: REQUEST_TYPES?.LIBRARY_CLOSURE,
            dateRange: {
              from: finalWhereFilters?.createdAt?.from?.$d,
              to: finalWhereFilters?.createdAt?.to?.$d,
            },
            actionTakenOn: {
              from: finalWhereFilters?.actionTakenOn?.from?.$d,
              to: finalWhereFilters?.actionTakenOn?.to?.$d,
            },
            status:
              finalWhereFilters?.status?.length > 0
                ? finalWhereFilters?.status
                : undefined,
            libraryIds: finalWhereFilters?.name,
            backUpLibraryIds: finalWhereFilters?.backUpLibName,
            closureRequestToDateRange: {
              to: finalWhereFilters?.toDate?.date?.$d,
            },
            closureRequestFromDateRange: {
              from: finalWhereFilters?.fromDate?.date?.$d,
            },
          },
          sort: sortedInfo?.column
            ? {
                field: sortedInfo?.field,
                order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
              }
            : initialAddressSort,
        },
      });
    }
    setIsApproveLoading(false);
  };

  const handleReject = async (id) => {
    setIsRejectLoading(true);
    const response = await updateRequestAdmin({
      variables: {
        where: {
          id,
        },
        data: {
          status: REQUEST_STATUS?.REJECTED?.value,
        },
      },
    });
    if (response?.data) {
      setIsClosureReqLoading(true);
      setIsRejectPrompts(false);
      setClosureReq(null);
      requestsAdmin({
        variables: {
          filter: {
            ...initialAddressFilter,
            skip:
              (paginationProp?.current - 1) * (paginationProp?.pageSize || 0),
            limit: paginationProp?.pageSize || LIMIT,
            search: closureReqSearchTerm,
            isArchived: isArchiveFilter,
            type: REQUEST_TYPES?.LIBRARY_CLOSURE,
            dateRange: {
              from: finalWhereFilters?.createdAt?.from?.$d,
              to: finalWhereFilters?.createdAt?.to?.$d,
            },
            actionTakenOn: {
              from: finalWhereFilters?.actionTakenOn?.from?.$d,
              to: finalWhereFilters?.actionTakenOn?.to?.$d,
            },
            status:
              finalWhereFilters?.status?.length > 0
                ? finalWhereFilters?.status
                : undefined,
            libraryIds: finalWhereFilters?.name,
            backUpLibraryIds: finalWhereFilters?.backUpLibName,
            closureRequestToDateRange: {
              to: finalWhereFilters?.toDate?.date?.$d,
            },
            closureRequestFromDateRange: {
              from: finalWhereFilters?.fromDate?.date?.$d,
            },
          },
          sort: sortedInfo?.column
            ? {
                field: sortedInfo?.field,
                order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
              }
            : initialAddressSort,
        },
      });
    }
    setIsRejectLoading(false);
  };

  const handleBackup = async (id) => {
    setIsBackupLibLoading(true);
    const response = await updateRequestAdmin({
      variables: {
        where: {
          id,
        },
        data: {
          isActive: !closureReq?.library?.isActive,
        },
      },
    });
    if (response?.data) {
      setIsClosureReqLoading(true);
      setIsBackupLibPrompts(false);
      setClosureReq(null);
      requestsAdmin({
        variables: {
          filter: {
            ...initialAddressFilter,
            skip:
              (paginationProp?.current - 1) * (paginationProp?.pageSize || 0),
            limit: paginationProp?.pageSize || LIMIT,
            search: closureReqSearchTerm,
            isArchived: isArchiveFilter,
            type: REQUEST_TYPES?.LIBRARY_CLOSURE,
            dateRange: {
              from: finalWhereFilters?.createdAt?.from?.$d,
              to: finalWhereFilters?.createdAt?.to?.$d,
            },
            actionTakenOn: {
              from: finalWhereFilters?.actionTakenOn?.from?.$d,
              to: finalWhereFilters?.actionTakenOn?.to?.$d,
            },
            status:
              finalWhereFilters?.status?.length > 0
                ? finalWhereFilters?.status
                : undefined,
            libraryIds: finalWhereFilters?.name,
            backUpLibraryIds: finalWhereFilters?.backUpLibName,
            closureRequestToDateRange: {
              to: finalWhereFilters?.toDate?.date?.$d,
            },
            closureRequestFromDateRange: {
              from: finalWhereFilters?.fromDate?.date?.$d,
            },
          },
          sort: sortedInfo?.column
            ? {
                field: sortedInfo?.field,
                order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
              }
            : initialAddressSort,
        },
      });
    }
    setIsBackupLibLoading(false);
  };

  const handleRaiseRequest = async (values) => {
    setIsRaiseRequestLoading(true);
    const response = await createRequestLibraryAdmin({
      variables: {
        data: {
          closureRequest: {
            fromDate: values?.closureDate?.[0]?.$d,
            toDate: values?.closureDate?.[1]?.$d,
          },
          description: values?.reason,
          libraryId: values?.libraryId,
          type: REQUEST_TYPES?.LIBRARY_CLOSURE,
        },
      },
    });
    if (response?.data) {
      setClosureReqList([]);
      setPaginationProp({ ...paginationProp, current: 1, skip: 0 });
      setIsClosureReqLoading(true);
      setIsRaiseRequestModalOpen(false);
      setClosureReq(null);
      form?.resetFields();
      requestsAdmin({
        variables: {
          filter: {
            ...initialAddressFilter,
            skip:
              (paginationProp?.current - 1) * (paginationProp?.pageSize || 0),
            limit: paginationProp?.pageSize || LIMIT,
            search: closureReqSearchTerm,
            isArchived: isArchiveFilter,
            type: REQUEST_TYPES?.LIBRARY_CLOSURE,
            dateRange: {
              from: finalWhereFilters?.createdAt?.from?.$d,
              to: finalWhereFilters?.createdAt?.to?.$d,
            },
            actionTakenOn: {
              from: finalWhereFilters?.actionTakenOn?.from?.$d,
              to: finalWhereFilters?.actionTakenOn?.to?.$d,
            },
            status:
              finalWhereFilters?.status?.length > 0
                ? finalWhereFilters?.status
                : undefined,
            libraryIds: finalWhereFilters?.name,
            backUpLibraryIds: finalWhereFilters?.backUpLibName,
            closureRequestToDateRange: {
              to: finalWhereFilters?.toDate?.date?.$d,
            },
            closureRequestFromDateRange: {
              from: finalWhereFilters?.fromDate?.date?.$d,
            },
          },
          sort: sortedInfo?.column
            ? {
                field: sortedInfo?.field,
                order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
              }
            : initialAddressSort,
        },
      });
    }
    setIsRaiseRequestLoading(false);
  };

  const handleUpdateRaiseRequest = async (values) => {
    setIsRaiseRequestLoading(true);
    const response = await updateRequestAdmin({
      variables: {
        where: {
          id: closureReq?.id,
        },
        data: {
          closureRequest: {
            fromDate: values?.closureDate?.[0]?.$d,
            toDate: values?.closureDate?.[1]?.$d,
          },
          description: values?.reason,
        },
      },
    });
    if (response?.data) {
      setClosureReqList([]);
      setPaginationProp({ ...paginationProp, current: 1, skip: 0 });
      setIsClosureReqLoading(true);
      setIsRaiseRequestModalOpen(false);
      setClosureReq(null);
      form?.resetFields();
      requestsAdmin({
        variables: {
          filter: {
            ...initialAddressFilter,
            skip:
              (paginationProp?.current - 1) * (paginationProp?.pageSize || 0),
            limit: paginationProp?.pageSize || LIMIT,
            search: closureReqSearchTerm,
            isArchived: isArchiveFilter,
            type: REQUEST_TYPES?.LIBRARY_CLOSURE,
            dateRange: {
              from: finalWhereFilters?.createdAt?.from?.$d,
              to: finalWhereFilters?.createdAt?.to?.$d,
            },
            actionTakenOn: {
              from: finalWhereFilters?.actionTakenOn?.from?.$d,
              to: finalWhereFilters?.actionTakenOn?.to?.$d,
            },
            ...(finalWhereFilters?.status?.length > 0 && {
              status: finalWhereFilters?.status,
            }),
            libraryIds: finalWhereFilters?.name,
            backUpLibraryIds: finalWhereFilters?.backUpLibName,
            closureRequestToDateRange: {
              to: finalWhereFilters?.toDate?.date?.$d,
            },
            closureRequestFromDateRange: {
              from: finalWhereFilters?.fromDate?.date?.$d,
            },
          },
          sort: sortedInfo?.column
            ? {
                field: sortedInfo?.field,
                order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
              }
            : initialAddressSort,
        },
      });
    }
    setIsRaiseRequestLoading(false);
  };

  const getFilterData = (confirm) => {
    setIsClosureReqLoading(true);
    requestsAdmin({
      variables: {
        filter: {
          ...initialAddressFilter,
          limit: paginationProp?.pageSize || LIMIT,
          search: closureReqSearchTerm,
          isArchived: isArchiveFilter,
          type: REQUEST_TYPES?.LIBRARY_CLOSURE,
          dateRange: {
            from: whereFilter?.createdAt?.from?.$d,
            to: whereFilter?.createdAt?.to?.$d,
          },
          actionTakenOn: {
            from: whereFilter?.actionTakenOn?.from?.$d,
            to: whereFilter?.actionTakenOn?.to?.$d,
          },
          status:
            whereFilter?.status?.length > 0 ? whereFilter?.status : undefined,
          libraryIds: whereFilter?.name,
          backUpLibraryIds: whereFilter?.backUpLibName,
          closureRequestToDateRange: {
            to: whereFilter?.toDate?.date?.$d,
          },
          closureRequestFromDateRange: {
            from: whereFilter?.fromDate?.date?.$d,
          },
        },
        sort: sortedInfo?.column
          ? {
              field: sortedInfo?.field,
              order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialAddressSort,
      },
    });
    setFinalWhereFilters({ ...finalWhereFilters, ...whereFilter });
    setExportFilter({ ...exportFilter, ...finalWhereFilters, ...whereFilter });
    setFinalFilterTags(filterTags);
    setPaginationProp({ ...paginationProp, skip: 0, current: 1 });
    if (confirm) {
      confirm();
    }
  };

  const handleReset = (clearFilters, dataIndex, confirm) => {
    const tagsCopy = {
      ...filterTags,
      [dataIndex]: [],
    };
    const whereFiltersCopy = {
      ...whereFilter,
      [dataIndex]: [],
    };
    setFilterVisible(false);
    setFinalFilterTags({ ...filterTags, ...tagsCopy });
    setFinalWhereFilters({ ...finalWhereFilters, ...whereFiltersCopy });
    setExportFilter({
      ...exportFilter,
      ...finalWhereFilters,
      ...whereFiltersCopy,
    });
    setIsClosureReqLoading(true);
    requestsAdmin({
      variables: {
        filter: {
          ...initialAddressFilter,
          limit: paginationProp?.pageSize || LIMIT,
          search: closureReqSearchTerm,
          isArchived: isArchiveFilter,
          type: REQUEST_TYPES?.LIBRARY_CLOSURE,
          dateRange: {
            from: whereFiltersCopy?.createdAt?.from?.$d,
            to: whereFiltersCopy?.createdAt?.to?.$d,
          },
          actionTakenOn: {
            from: whereFiltersCopy?.actionTakenOn?.from?.$d,
            to: whereFiltersCopy?.actionTakenOn?.to?.$d,
          },
          status:
            whereFiltersCopy?.status?.length > 0
              ? whereFiltersCopy?.status
              : undefined,
          libraryIds: whereFiltersCopy?.name,
          backUpLibraryIds: whereFiltersCopy?.backUpLibName,
          closureRequestToDateRange: {
            to: whereFiltersCopy?.toDate?.date?.$d,
          },
          closureRequestFromDateRange: {
            from: whereFiltersCopy?.fromDate?.date?.$d,
          },
        },
        sort: sortedInfo?.column
          ? {
              field: sortedInfo?.field,
              order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialAddressSort,
      },
    });
    setPaginationProp({ ...paginationProp, skip: 0, current: 1 });
    clearFilters();
    setFilterVisible(false);
    if (confirm) {
      confirm();
    }
  };

  useEffect(() => {
    if (filterVisible) {
      setFilterList([]);
      setFilterLoading(true);
      setIsEmptyFilterList(false);
      setFilterTags({ ...filterTags, ...finalFilterTags });
      setWhereFilter({ ...whereFilter, ...finalWhereFilters });
      setFilterIsEnd(false);
      const optionsCopy = [];

      switch (filterIndex) {
        case 'status':
          forEach(STATUS_FILTER, (item) => {
            optionsCopy?.push({ label: item?.label, key: item?.key });
          });
          setFilterList(optionsCopy);
          setFilterLoading(false);
          break;

        case 'name':
          libraries({
            variables: {
              filter: initialTableFilter,
              sort: initialTableSort,
            },
          });
          break;

        case 'backUpLibName':
          libraries({
            variables: {
              filter: initialTableFilter,
              sort: initialTableSort,
            },
          });
          break;

        default:
          break;
      }
    }
    if (!filterVisible) {
      setFilterTags(finalFilterTags);
      setWhereFilter(finalWhereFilters);
      setFilterSearch('');
    }
  }, [filterVisible]);

  const onFilterScroll = (event, dataIndex) => {
    if (filterScrollDebounceJob) {
      filterScrollDebounceJob?.cancel();
      filterScrollDebounceJob = null;
    }
    if (!isLibrariesLoading && !filterIsEnd) {
      const target = event?.target;
      const currentLength = filterList?.length;
      if (
        target?.scrollTop + target?.offsetHeight >=
        target?.scrollHeight - 5
      ) {
        filterScrollDebounceJob = debounce(() => {
          setFilterLoading(true);
          switch (dataIndex) {
            case 'name':
              libraries({
                variables: {
                  filter: {
                    limit: LIMIT,
                    skip: currentLength,
                    search: filterSearch,
                  },
                  sort: initialTableSort,
                },
              });
              break;

            case 'backUpLibName':
              libraries({
                variables: {
                  filter: {
                    limit: LIMIT,
                    skip: currentLength,
                    search: filterSearch,
                  },
                  sort: initialTableSort,
                },
              });
              break;

            default:
              break;
          }
        }, 500);
        filterScrollDebounceJob();
      }
    }
  };

  const handleFilterSearch = (value, dataIndex) => {
    setFilterSearch(value);
    setFilterIsEnd(false);
    setFilterLoading(true);
    setFilterList([]);
    switch (dataIndex) {
      case 'name':
        libraries({
          variables: {
            filter: {
              ...initialTableFilter,
              limit: LIMIT,
              search: value,
            },
            sort: initialTableSort,
          },
        });
        break;

      case 'backUpLibName':
        libraries({
          variables: {
            filter: {
              ...initialTableFilter,
              limit: LIMIT,
              search: value,
            },
            sort: initialTableSort,
          },
        });
        break;

      default:
        break;
    }
  };

  const changeFilter = (newFilterObj, dataIndex) => {
    const {
      target: { value },
    } = newFilterObj;

    const filterTagsCopy = { ...filterTags };
    let newTagList;
    if (whereFilter?.[dataIndex]?.includes(value?.key)) {
      newTagList = {
        ...filterTagsCopy,
        [dataIndex]: filter(filterTagsCopy?.[dataIndex], (item) => {
          if (item?.label) {
            return item?.key !== value?.key;
          }
          return item !== value;
        }),
      };
    } else {
      newTagList = {
        ...filterTagsCopy,
        [dataIndex]: filterTagsCopy?.[dataIndex]
          ? [...filterTagsCopy?.[dataIndex], value]
          : [value],
      };
    }

    const updatedWhereFilter = {
      ...whereFilter,
      [dataIndex]: newTagList?.[dataIndex]?.map((item) => item?.key),
    };
    setWhereFilter(updatedWhereFilter);
    setFilterTags(newTagList);
  };

  const handleDeleteFilter = (value, dataIndex) => {
    let newTagList;

    if (whereFilter?.[dataIndex]?.includes(value?.key)) {
      newTagList = {
        ...filterTags,
        [dataIndex]: filter(filterTags?.[dataIndex], (item) => {
          if (item?.key) {
            return item?.key !== value?.key;
          }
          return item !== value;
        }),
      };
    }

    const updatedWhereFilter = {
      ...whereFilter,
      [dataIndex]: newTagList?.[dataIndex]?.map((item) => item?.key),
    };
    setWhereFilter(updatedWhereFilter);
    setFilterTags(newTagList);
  };

  const onRangePickerChange = (values, dataIndex) => {
    const rangeObj = {
      from: values[0]?.startOf('day'),
      to: values?.[1]?.endOf('day'),
    };

    const filtersCopy = {
      ...whereFilter,
      [dataIndex]: rangeObj,
    };
    if (!values?.length) {
      delete whereFilter?.[dataIndex];
    }
    setWhereFilter(filtersCopy);
  };

  const onDatePickerChange = (values, dataIndex) => {
    let date;
    if (dataIndex === 'fromDate') {
      date = {
        date: values?.startOf('day'),
      };
    }
    if (dataIndex === 'toDate') {
      date = {
        date: values?.endOf('day'),
      };
    }

    const filtersCopy = {
      ...whereFilter,
      [dataIndex]: date,
    };
    if (!values?.length) {
      delete whereFilter?.[dataIndex];
    }
    setWhereFilter(filtersCopy);
  };

  const filterPopup = (dataIndex, showSearch = true) => ({
    filterDropdown: ({ confirm, clearFilters }) => {
      if (dataIndex === 'createdAt' || dataIndex === 'actionTakenOn') {
        return (
          <div className="custom-filter-dropdown">
            <RangePicker
              allowClear={false}
              format={DATE_FORMATS?.REGULAR}
              onChange={(values) => onRangePickerChange(values, dataIndex)}
              value={
                whereFilter?.[dataIndex]?.from
                  ? [
                      whereFilter?.[dataIndex]?.from,
                      whereFilter?.[dataIndex]?.to,
                    ]
                  : null
              }
            />
            <Divider className="divider-filter" />
            <div className="d-flex justify-center">
              <Button
                size="small"
                className="reset-button"
                id="roles-filter-reset"
                onClick={() => handleReset(clearFilters, dataIndex, confirm)}
              >
                Reset
              </Button>
              <Button
                size="small"
                className="common-button ok-button"
                id="roles-filter-ok"
                type="primary"
                onClick={() => getFilterData(confirm, dataIndex)}
              >
                Ok
              </Button>
            </div>
          </div>
        );
      }

      if (dataIndex === 'fromDate' || dataIndex === 'toDate') {
        return (
          <div className="custom-filter-dropdown">
            <DatePicker
              allowClear={false}
              format={DATE_FORMATS?.REGULAR}
              onChange={(values) => onDatePickerChange(values, dataIndex)}
              value={
                whereFilter?.[dataIndex]?.date
                  ? whereFilter?.[dataIndex]?.date
                  : null
              }
              className="full-width"
            />
            <Divider className="divider-filter" />
            <div className="d-flex justify-center">
              <Button
                size="small"
                className="reset-button"
                id="roles-filter-reset"
                onClick={() => handleReset(clearFilters, dataIndex, confirm)}
              >
                Reset
              </Button>
              <Button
                size="small"
                className="common-button ok-button"
                id="roles-filter-ok"
                type="primary"
                onClick={() => getFilterData(confirm, dataIndex)}
              >
                Ok
              </Button>
            </div>
          </div>
        );
      }

      return (
        <div className="custom-filter-dropdown">
          <LoaderComponent
            spinning={filterLoading}
            setHeight={35}
            size="medium"
          >
            {showSearch && (
              <SearchComponent
                className="filter-search"
                id="search-container-id-roles"
                placeholder="Search..."
                name={dataIndex}
                query={filterSearch}
                setQuery={setFilterSearch}
                getData={(value) => handleFilterSearch(value, dataIndex)}
              />
            )}
            {filterTags?.[dataIndex]?.length > 0 && (
              <div className="filter-tags-wrapper">
                {map(filterTags?.[dataIndex], (item) => (
                  <Tag
                    key={item?.key}
                    closable
                    onClose={() => handleDeleteFilter(item, dataIndex)}
                    className="filter-tag"
                  >
                    <span>{item?.label}</span>
                  </Tag>
                ))}
              </div>
            )}
            <div
              className="filter-checkboxes"
              onScroll={(e) => onFilterScroll(e, dataIndex)}
            >
              {filterList?.length > 0
                ? map(filterList, (item) => (
                    <div className="single-checkbox-div" key={item?.key}>
                      <Checkbox
                        value={item}
                        checked={whereFilter?.[dataIndex]?.includes(item?.key)}
                        key={item?.key || item}
                        onChange={(e) => changeFilter(e, dataIndex)}
                        className="single-checkbox"
                      >
                        <span>{item?.label}</span>
                      </Checkbox>
                    </div>
                  ))
                : isEmptyFilterList && (
                    <Empty image={Empty?.PRESENTED_IMAGE_SIMPLE} />
                  )}
            </div>
          </LoaderComponent>
          <Divider className="divider-filter" />
          <div className="d-flex justify-center">
            <Button
              size="small"
              className="reset-button"
              id="roles-filter-reset"
              onClick={() => handleReset(clearFilters, dataIndex, confirm)}
            >
              Reset
            </Button>
            <Button
              size="small"
              className="common-button ok-button"
              id="roles-filter-ok"
              type="primary"
              onClick={() => getFilterData(confirm, dataIndex)}
            >
              Ok
            </Button>
          </div>
        </div>
      );
    },
    filterIcon: () =>
      finalWhereFilters?.[dataIndex]?.length > 0 ||
      !isEmpty(finalWhereFilters?.[dataIndex]) ? (
        <FilterFilled className="filter-icon filter-filled" />
      ) : (
        <FilterOutlined className="filter-icon" />
      ),
    onFilterDropdownOpenChange: (visible) => {
      setFilterIndex(dataIndex);
      setFilterVisible(visible);
    },
  });

  const columns = [
    {
      title: 'LIBRARY NAME',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      sorter: true,
      width: 250,
      align: 'left',
      className: `max-width-column`,
      sortOrder: sortedInfo?.columnKey === 'name' && sortedInfo?.order,
      onCell: (record) => ({
        onClick: () =>
          isLibraryPermission &&
          record?.library?.id &&
          history?.push(
            `${ROUTES?.MASTER_LISTS}${ROUTES?.LIBRARY}${ROUTES?.BASIC_DETAILS}/view/${record?.library?.id}/${record?.library?.libraryNumber}`,
          ),
        className: `${
          isLibraryPermission && record?.library?.id && 'pointer table-cell'
        }`,
      }),
      render: (_, record) => (
        <>
          <div className="table-data-with-id">{record?.library?.name}</div>
          <div className="font-size-12">({record?.library?.libraryNumber})</div>
        </>
      ),
      ...filterPopup('name'),
    },
    {
      title: 'REQUEST ID',
      dataIndex: 'id',
      key: 'id',
      ellipsis: true,
      width: 170,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) => record?.requestNumber,
    },
    {
      title: 'REQUEST DATE',
      dataIndex: 'requestDate',
      key: 'requestDate',
      ellipsis: true,
      width: 150,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) =>
        formatDate(record?.createdAt, DATE_FORMATS?.REGULAR) || '-',
      ...filterPopup('createdAt'),
    },
    {
      title: 'FROM DATE',
      dataIndex: 'fromDate',
      key: 'fromDate',
      ellipsis: true,
      width: 150,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) =>
        formatDate(record?.metaData?.fromDate, DATE_FORMATS?.REGULAR) || '-',
      ...filterPopup('fromDate'),
    },
    {
      title: 'TO DATE',
      dataIndex: 'toDate',
      key: 'toDate',
      ellipsis: true,
      width: 150,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) =>
        formatDate(record?.metaData?.toDate, DATE_FORMATS?.REGULAR) || '-',
      ...filterPopup('toDate'),
    },
    {
      title: 'REASON',
      dataIndex: 'reason',
      key: 'reason',
      ellipsis: true,
      width: 250,
      align: 'left',
      className: 'max-width-column table-tooltip-cell',
      render: (_, record) => (
        <Tooltip
          title={record?.description ? record?.description : 'No Description'}
          placement="topLeft"
          trigger="click"
          className="pointer"
        >
          {record?.description || '-'}
        </Tooltip>
      ),
    },
    {
      title: 'ACTION TAKEN ON',
      dataIndex: 'actionTakenOn',
      key: 'actionTakenOn',
      ellipsis: true,
      width: 200,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) =>
        formatDate(record?.updatedAt, DATE_FORMATS?.WITH_TIME) || '-',
      ...filterPopup('actionTakenOn'),
    },
    {
      title: 'BACKUP LIBRARY NAME',
      dataIndex: 'backupLibName',
      key: 'backupLibName',
      ellipsis: true,
      width: 250,
      align: 'left',
      className: `max-width-column`,
      onCell: (record) => ({
        onClick: () =>
          isLibraryPermission &&
          record?.library?.backUpLibrary?.id &&
          history?.push(
            `${ROUTES?.MASTER_LISTS}${ROUTES?.LIBRARY}${ROUTES?.BASIC_DETAILS}/view/${record?.library?.backUpLibrary?.id}/${record?.library?.backUpLibrary?.libraryNumber}`,
          ),
        className: `${
          isLibraryPermission &&
          record?.library?.backUpLibrary?.id &&
          'pointer table-cell'
        }`,
      }),
      render: (_, record) =>
        record?.library?.backUpLibrary?.name ? (
          <>
            <div className="table-data-with-id">
              {record?.library?.backUpLibrary?.name}
            </div>
            <div className="font-size-12">
              ({record?.library?.backUpLibrary?.libraryNumber})
            </div>
          </>
        ) : (
          '-'
        ),
      ...filterPopup('backUpLibName'),
    },
    {
      title: 'STATUS',
      dataIndex: 'status',
      key: 'status',
      ellipsis: true,
      width: 150,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) => (
        <Badge
          color={REQUEST_STATUS?.[record?.status]?.color}
          text={REQUEST_STATUS?.[record?.status]?.text}
        />
      ),
      ...filterPopup('status', false),
    },
    {
      title: 'ACTIONS',
      dataIndex: 'actions',
      key: 'actions',
      ellipsis: true,
      width: 150,
      // eslint-disable-next-line no-undef
      fixed: window.innerWidth > BREAKPOINTS.desktop ? 'right' : false,
      render: (_, record) => {
        const isFutureToDate =
          !dayjs(record?.metaData?.toDate)?.isSame(dayjs(), 'day') &&
          !dayjs(record?.metaData?.toDate)?.isAfter(dayjs(), 'day');

        const activateBtnStatus =
          record?.library?.isActive &&
          !record?.metaData?.isProcessed &&
          !record?.metaData?.isReActivateBackUp;
        const deActivateBtnStatus =
          !record?.library?.isActive &&
          !record?.metaData?.isProcessed &&
          !record?.metaData?.isReActivateBackUp;
        const reActivateBtnStatus =
          record?.library?.isActive &&
          record?.metaData?.isProcessed &&
          record?.metaData?.isReActivateBackUp;
        const expiredBtnStatus =
          !record?.library?.isActive &&
          record?.metaData?.isProcessed &&
          record?.metaData?.isReActivateBackUp;

        return (
          <>
            <div className="action-button">
              {isArchiveFilter ? (
                <Tooltip title="Unarchive" placement="top" zIndex={4}>
                  <UnarchiveIcon
                    onClick={() => {
                      setIsArchivePrompts(true);
                      setClosureReq(record);
                    }}
                    className="mr-16 pointer svg-icon"
                  />
                </Tooltip>
              ) : (
                <>
                  <Tooltip title="Edit" placement="top" zIndex={4}>
                    <EditIcon
                      onClick={() => {
                        setClosureReq(record);
                        setIsRaiseRequestModalOpen(true);
                      }}
                      className="mr-16 pointer svg-icon"
                    />
                  </Tooltip>
                  {record?.status === REQUEST_STATUS?.PENDING?.value && (
                    <>
                      <div className="mr-16 d-flex">
                        <Tooltip
                          title="Approve"
                          placement="top"
                          className="table-action-button"
                          zIndex={4}
                        >
                          <Button
                            disabled={
                              record?.status !== REQUEST_STATUS?.PENDING?.value
                            }
                            type="link"
                            onClick={() => {
                              setIsApprovePrompts(true);
                              setClosureReq(record);
                            }}
                          >
                            <ApproveIcon
                              className={
                                record?.status !==
                                REQUEST_STATUS?.PENDING?.value
                                  ? 'svg-icon-grey'
                                  : 'svg-icon'
                              }
                            />
                          </Button>
                        </Tooltip>
                      </div>
                      <div className="mr-16 d-flex">
                        <Tooltip
                          title="Reject"
                          placement="top"
                          className="table-action-button"
                          zIndex={4}
                        >
                          <Button
                            disabled={
                              record?.status !== REQUEST_STATUS?.PENDING?.value
                            }
                            type="link"
                            onClick={() => {
                              setIsRejectPrompts(true);
                              setClosureReq(record);
                            }}
                          >
                            <RejectIcon
                              className={
                                record?.status !==
                                REQUEST_STATUS?.PENDING?.value
                                  ? 'svg-icon-grey'
                                  : 'svg-icon'
                              }
                            />
                          </Button>
                        </Tooltip>
                      </div>
                    </>
                  )}
                  {record?.status === REQUEST_STATUS?.REJECTED?.value && (
                    <Tag color="magenta" className="mr-16">
                      Rejected
                    </Tag>
                  )}
                  {record?.status === REQUEST_STATUS?.APPROVED?.value && (
                    <div className="mr-16">
                      <Button
                        type="primary"
                        size="small"
                        className="table-action-primary-btn"
                        onClick={() => {
                          if (!expiredBtnStatus) {
                            setIsBackupLibPrompts(true);
                            setClosureReq(record);
                          }
                        }}
                        disabled={
                          expiredBtnStatus
                            ? true
                            : isFutureToDate
                            ? !deActivateBtnStatus
                            : false
                        }
                      >
                        {activateBtnStatus && 'Activate Backup'}
                        {deActivateBtnStatus && 'De-Activate Backup'}
                        {reActivateBtnStatus && 'Re-Activate Backup'}
                        {expiredBtnStatus && 'Expired'}
                      </Button>
                    </div>
                  )}
                  <div className="mr-16 d-flex">
                    <Tooltip
                      title="Archive"
                      placement="top"
                      zIndex={4}
                      className="table-action-button"
                    >
                      <Button
                        // disabled={isFutureToDate}
                        type="link"
                        onClick={() => {
                          setIsArchivePrompts(true);
                          setClosureReq(record);
                        }}
                      >
                        <ArchiveIcon className="svg-icon" />
                      </Button>
                    </Tooltip>
                  </div>
                </>
              )}
            </div>
          </>
        );
      },
    },
  ]?.filter((item) => {
    if (item?.dataIndex === 'actions' && !hasUpdatePermission) {
      return item?.hidden;
    }
    return !item?.hidden;
  });

  const locale = {
    emptyText: isEmptyClosureReqList ? '' : <span />,
  };

  return (
    <>
      <RaiseClosureRequestModal
        form={form}
        isModalOpen={isRaiseRequestModalOpen}
        setIsModalOpen={setIsRaiseRequestModalOpen}
        onFinish={closureReq ? handleUpdateRaiseRequest : handleRaiseRequest}
        loadings={isRaiseRequestLoading}
        closureReq={closureReq}
        setClosureReq={setClosureReq}
        title={
          closureReq
            ? 'Update Temporary Closure Request'
            : 'Temporary Closure Request'
        }
      />
      <Modal
        title="Caution"
        okText="Yes"
        cancelText="No"
        open={isArchivePrompts}
        onOk={() => handleArchive(closureReq?.id, closureReq?.isArchived)}
        onCancel={() => {
          setIsArchivePrompts(false);
          setClosureReq(null);
        }}
        okButtonProps={{ loading: isArchiveLoading }}
      >
        Are you sure you want to
        <strong>
          {' '}
          {!closureReq?.isArchived
            ? ARCHIVING_STATE?.ARCHIVED
            : ARCHIVING_STATE?.UNARCHIVED}{' '}
        </strong>
        this request?
      </Modal>
      <Modal
        title="Caution"
        okText="Yes"
        cancelText="No"
        open={isApprovePrompts}
        onOk={() => handleApprove(closureReq?.id)}
        onCancel={() => {
          setIsApprovePrompts(false);
          setClosureReq(null);
        }}
        okButtonProps={{ loading: isApproveLoading }}
      >
        Are you sure you want to
        <strong> Approve </strong>
        this request?
      </Modal>
      <Modal
        title="Caution"
        okText="Yes"
        cancelText="No"
        open={isRejectPrompts}
        onOk={() => handleReject(closureReq?.id)}
        onCancel={() => {
          setIsRejectPrompts(false);
          setClosureReq(null);
        }}
        okButtonProps={{ loading: isRejectLoading }}
      >
        Are you sure you want to
        <strong> Reject </strong>
        this request?
      </Modal>
      <Modal
        title="Caution"
        okText="Yes"
        cancelText="No"
        open={isBackupLibPrompts}
        onOk={() => handleBackup(closureReq?.id)}
        onCancel={() => {
          setIsBackupLibPrompts(false);
          setClosureReq(null);
        }}
        okButtonProps={{ loading: isBackupLibLoading }}
      >
        Are you sure you want to
        <strong>
          {' '}
          {closureReq?.library?.isActive &&
            !closureReq?.metaData?.isProcessed &&
            !closureReq?.metaData?.isReActivateBackUp &&
            'Activate Backup '}
          {!closureReq?.library?.isActive &&
            !closureReq?.metaData?.isProcessed &&
            !closureReq?.metaData?.isReActivateBackUp &&
            'De-Activate Backup '}
          {closureReq?.library?.isActive &&
            closureReq?.metaData?.isProcessed &&
            closureReq?.metaData?.isReActivateBackUp &&
            'Re-Activate Backup '}
        </strong>
        library?
      </Modal>
      <div className="d-flex justify-end mb-16 flex-wrap">
        {hasUpdatePermission && (
          <div className="mr-16 d-flex align-center">
            <h4 className="m-0 mr-8">Only Archived</h4>{' '}
            <Switch
              size="small"
              onChange={(checked) => setIsArchiveFilter(checked)}
            />
          </div>
        )}
        <SearchComponent
          className="search-box"
          query={query}
          setQuery={setQuery}
          getData={handleSearch}
        />
      </div>
      <CommonTable
        locale={locale}
        columns={columns}
        data={closureReqList || []}
        loading={isClosureReqLoading}
        onChange={handleTableChange}
        paginationConfig={paginationProp}
        rowKey={(record) => record?.id}
      />
    </>
  );
};

export default ClosureRequestsTable;
